import { useState } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import AttachSageEntityModal from './AttachSageEntityModal';

// ---------------------------------------------------------------------------
// THE HOLDING COMPANY, EXPLAINED — mostly an explanation, with one control.
//
// A deal's Sage money lives under two kinds of location: the OPERATING location
// holds the asset, the ENTITY that owns it holds the borrowing and the equity.
// Harvus attaches the entity automatically (SageLocations.ParentID records the
// roll-up), so its postings are already inside the deal's Total Cost and its
// actual IRR.
//
// THAT IS EXACTLY WHY THIS EXISTS. A figure that silently grew by $9.3M of debt
// nobody tagged is a figure nobody trusts. The strip says which entities are
// counted and what each contributes.
//
// A CONFLICT IS DELIBERATELY NOT SHOWN HERE. It used to raise a red banner on
// every deal competing for the entity, which reads as an incident on a deal
// that has none — and an entity that legitimately spans several deals (a
// personal holding company) can never be resolved in Sage, so the banner was
// permanent and its readers learned to scroll past it. The refusal is still
// RECORDED and still VISIBLE, in the two places somebody can act on it: Neil
// and Kurt are emailed once per conflict, and Settings → Sage Import →
// “Review locations & tag entities” names every open one with its deals.
// `conflicts` is still returned by the API for that panel and for anything
// added later; this surface simply does not render it.
//
// THE ONE ACTION IS THE FALLBACK, AND IT HAS TO BE REACHABLE ON A DEAL WITH
// NOTHING ATTACHED — which is the whole case it exists for. The derivation
// starts at a location a capital item has tagged, so a deal that has tagged
// none has no entity, no strip, and no way to say the borrowing is missing.
// So an Admin looking at such a deal gets ONE muted link, exactly the shape
// DealNaicsChip already uses two lines above this in Deals.jsx: gating the
// affordance on the deal already having what the affordance provides hides it
// on precisely the deals that need it.
//
// Everyone else still sees NOTHING when nothing has happened. A row of "none"
// on every deal in the book would be noise on the deals that have no holding
// company at all, which is most of them.
// ---------------------------------------------------------------------------
export default function DealSageEntitiesStrip({ sageEntities, dealId, dealName, canEdit = false, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [local, setLocal]   = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr]       = useState(null);

  // The server's answer is authoritative the moment we have one: a write
  // returns the whole refreshed block, so the strip re-renders from it rather
  // than waiting on the deal reload it also asks for.
  const data = local || sageEntities;
  if (!data || !data.available) return null;
  const entities  = data.entities  || [];

  const applied = (res) => { setLocal(res); onChanged?.(); };

  const remove = async (e) => {
    setBusyId(e.dealSageEntityID); setErr(null);
    try {
      applied(await api.del(`deals/${dealId}/sage-entities/${e.dealSageEntityID}`));
    } catch (ex) {
      // Verbatim from the API — for an 'auto' row it names the Sage-side fix
      // that actually holds, which a locally-invented message would lose.
      setErr(ex.message || 'Could not remove that entity.');
    } finally {
      setBusyId(null);
    }
  };

  const modal = adding && (
    <AttachSageEntityModal
      dealId={dealId}
      dealName={dealName}
      onClose={() => setAdding(false)}
      onAttached={applied}
    />
  );

  if (!entities.length) {
    if (!canEdit || !dealId) return null;
    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => setAdding(true)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, color: B.textLt, textDecoration: 'underline',
            }}
            title="Attach the LLC or holding company that carries this deal's borrowing in Sage"
          >
            + Sage entity
          </button>
        </div>
        {modal}
      </>
    );
  }

  return (
    <>
      <div style={{
        marginBottom: 16, border: `1px solid ${B.border}`, borderRadius: 10,
        background: B.surfaceMuted, padding: '10px 12px',
      }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 12, marginBottom: 6,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase', color: B.textMd,
          }}>
            Sage entities counted in this deal
          </div>
          {canEdit && dealId && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontSize: 12, color: B.textLt, textDecoration: 'underline',
              }}
            >
              + Add another
            </button>
          )}
        </div>
        {entities.map(e => (
          <EntityRow
            key={e.dealSageEntityID}
            entity={e}
            canEdit={canEdit && !!dealId}
            busy={busyId === e.dealSageEntityID}
            onRemove={() => remove(e)}
          />
        ))}
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 6 }}>
          Attached automatically because a location on this deal rolls up to it in Sage,
          unless marked “added by hand”. Their postings are included in the deal’s Total
          Cost and actual IRR.
        </div>
        {err && <div style={{ fontSize: 12, color: B.red, marginTop: 6, lineHeight: 1.45 }}>{err}</div>}
      </div>
      {modal}
    </>
  );
}

function EntityRow({ entity: e, canEdit, busy, onRemove }) {
  // SageDebt arrives as a SIGNED GL balance and the client negates it once —
  // the same rule the deal header applies. NULL means "no breakout", which is
  // deliberately not 0: rendering $0 would claim the entity carries no debt
  // when what we actually have is no answer.
  const debt = e.sageDebt == null ? null : -Number(e.sageDebt);
  const byHand = e.source === 'user';
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 13, padding: '2px 0' }}>
      <span style={{ fontWeight: 600, color: B.text }}>
        {e.displayName || e.sageCode}
      </span>
      <span style={{ fontSize: 11, color: B.textLt, fontFamily: 'monospace' }}>{e.sageCode}</span>
      {/* WHICH MASTER THIS CAME FROM. An entity attached in its own right has no
          location at all (Boggy Creek, held entirely through its LLC), so
          "check the Locations import" is the wrong advice for it — and the row
          is otherwise indistinguishable from one that does have a location. */}
      {e.kind === 'entity' && (
        <span style={{
          fontSize: 10, letterSpacing: 0.3, textTransform: 'uppercase',
          color: B.textLt, border: `1px solid ${B.border}`, borderRadius: 999, padding: '1px 6px',
        }}>Entity</span>
      )}
      {debt != null && debt !== 0 && (
        <span style={{ color: B.textMd }}>
          <span style={{ fontWeight: 600 }}>Debt:</span> {fmt.currency(debt)}
        </span>
      )}
      {e.sageTotalCost != null && Number(e.sageTotalCost) !== 0 && (
        <span style={{ color: B.textMd }}>
          <span style={{ fontWeight: 600 }}>Total Cost:</span> {fmt.currency(e.sageTotalCost)}
        </span>
      )}
      {byHand && (
        <span style={{ fontSize: 11, color: B.textLt }} title={e.notes || undefined}>
          added by hand{e.notes ? ' ⓘ' : ''}
        </span>
      )}
      {/* Only a hand-made row offers Remove. An 'auto' row is derived, so the
          next reconcile — which runs on every deal-detail save — puts it
          straight back; a button that undoes itself within minutes is worse
          than no button. The API refuses it too, and says where the real fix
          is. */}
      {canEdit && byHand && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          style={{
            marginLeft: 'auto', background: 'none', border: 'none', padding: 0,
            cursor: busy ? 'default' : 'pointer', fontSize: 11, color: B.textLt,
            textDecoration: 'underline',
          }}
        >
          {busy ? 'Removing…' : 'Remove'}
        </button>
      )}
    </div>
  );
}
